import { useState } from 'react';
import { RefreshCw, Image, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { populateAllMissingCachedCovers, getCoverCacheStats } from '@/utils/populateCachedCovers';
import { supabase } from '@/integrations/supabase/client';

export function CoverCacheManager() {
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [result, setResult] = useState<{ processed: number; succeeded: number; failed: number } | null>(null);
  const [stats, setStats] = useState<{ totalWithCovers: number; totalCached: number; totalMissing: number; percentCached: number } | null>(null);
  const { toast } = useToast();

  const loadStats = async () => {
    try {
      const cacheStats = await getCoverCacheStats();
      setStats(cacheStats);
    } catch (error) {
      console.error('Failed to load stats:', error);
      toast({
        title: 'Error',
        description: 'Failed to load cover cache statistics',
        variant: 'destructive'
      });
    }
  };

  const startPopulating = async () => {
    try {
      setProcessing(true);
      setProgress({ current: 0, total: 0 });
      setResult(null);

      toast({
        title: 'Starting Cover Cache',
        description: 'Processing books in background...'
      });

      const populateResult = await populateAllMissingCachedCovers(50, (current, total, batchResult) => {
        setProgress({ current, total });
        if (batchResult) {
          setResult(batchResult);
        }
      });

      setResult(populateResult);
      await loadStats();

      toast({
        title: 'Cover Caching Complete',
        description: `Processed ${populateResult.processed} books. ${populateResult.succeeded} succeeded, ${populateResult.failed} failed.`,
        variant: populateResult.failed === 0 ? 'default' : 'destructive'
      });
    } catch (error) {
      console.error('Error populating covers:', error);
      toast({
        title: 'Error',
        description: 'Failed to populate cached covers',
        variant: 'destructive'
      });
    } finally {
      setProcessing(false);
    }
  };

  const triggerBackgroundJob = async () => {
    try {
      toast({
        title: 'Triggering Background Job',
        description: 'Starting background cover caching...'
      });

      const { data, error } = await supabase.functions.invoke('cache-covers-background');

      if (error) {
        throw error;
      }

      toast({
        title: 'Background Job Complete',
        description: `Processed ${data.processed} books. ${data.succeeded} succeeded, ${data.failed} failed.`
      });

      await loadStats();
    } catch (error) {
      console.error('Error triggering background job:', error);
      toast({
        title: 'Error',
        description: 'Failed to trigger background job',
        variant: 'destructive'
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Image className="w-5 h-5" />
          Cover Cache Manager
        </CardTitle>
        <CardDescription>
          Populate cached covers for faster loading. Cached covers load instantly from storage.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stats */}
        {stats && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Cache Progress</span>
              <Badge variant={stats.percentCached > 80 ? 'default' : 'secondary'}>
                {stats.percentCached}%
              </Badge>
            </div>
            <Progress value={stats.percentCached} className="h-2" />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{stats.totalCached.toLocaleString()} cached</span>
              <span>{stats.totalMissing.toLocaleString()} remaining</span>
            </div>
          </div>
        )}

        {/* Progress */}
        {processing && progress.total > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm">Processing...</span>
              <span className="text-sm text-muted-foreground">
                {progress.current} / {progress.total}
              </span>
            </div>
            <Progress value={(progress.current / progress.total) * 100} className="h-2" />
          </div>
        )}

        {/* Result */}
        {result && !processing && (
          <div className="flex items-center gap-4 p-3 bg-muted rounded-lg">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-sm">{result.succeeded} succeeded</span>
            </div>
            {result.failed > 0 && (
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-destructive" />
                <span className="text-sm">{result.failed} failed</span>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            onClick={loadStats}
            variant="outline"
            disabled={processing}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh Stats
          </Button>
          <Button
            onClick={startPopulating}
            disabled={processing}
          >
            {processing && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
            Populate Covers (1000)
          </Button>
          <Button
            onClick={triggerBackgroundJob}
            variant="secondary"
            disabled={processing}
          >
            Background Job (100)
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">
          Tip: Use "Populate Covers" for large batches (1000 books) or "Background Job" for smaller incremental updates (100 books).
        </p>
      </CardContent>
    </Card>
  );
}
